import type { Accessor } from 'solid-js';
import type { ConfigFeatureFlagValues, FeatureFlagSource } from '../types';
import {
  getFlags,
  getSourceFlagName,
  type FeatureFlagDefinitions,
} from '../featureFlags';

/**
 * Feature flag source backed by static values, for example from a
 * per-environment config file.
 *
 * Answers synchronously, also before `initialize` resolves, so there is no
 * "off" flash at startup. The values are read through an accessor, so flags
 * re-evaluate when they change, which lets an app layer its own overrides on top.
 */
export class ConfigFeatureFlagProvider<TFlag extends string = string>
  implements FeatureFlagSource<TFlag>
{
  public readonly name = 'config';

  constructor(
    private readonly definitions: FeatureFlagDefinitions<TFlag>,
    private readonly values: Accessor<ConfigFeatureFlagValues>
  ) {}

  public initialize(): Promise<void> {
    return Promise.resolve();
  }

  public hasFlag(flag: TFlag): boolean {
    const name = getSourceFlagName(this.definitions, flag, 'config');
    return name !== undefined && (this.values()[name] ?? false);
  }

  public enabledFlags(): readonly TFlag[] {
    return getFlags(this.definitions).filter((flag) => this.hasFlag(flag));
  }
}
